import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const url = (import.meta as any).env?.VITE_SUPABASE_URL as string | undefined;
const anon = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string | undefined;

// Keep one instance across HMR reloads
const globalKey = '__supabase_singleton__';

// Derive Supabase's default storage key pattern to ensure PKCE verifier is found
const ref = url?.match(/^https:\/\/([^.]*)\.supabase\.co/i)?.[1];
const DEFAULT_STORAGE_KEY = ref ? `sb-${ref}-auth-token` : 'coachd-auth';

// Create singleton client with Supabase's expected storage key
const supabase: SupabaseClient | null = (() => {
  if (!url || !anon) return null;

  // Use existing instance if available (HMR case)
  const existing = (globalThis as any)[globalKey];
  if (existing) return existing;

  // Create new client with default Supabase storage key pattern
  const client = createClient(url, anon, {
    auth: {
      storageKey: DEFAULT_STORAGE_KEY,
      persistSession: true,
      autoRefreshToken: true,
      flowType: 'pkce',
      detectSessionInUrl: false, // we handle callback manually
    },
  });

  // Store in global for HMR persistence
  if ((import.meta as any).env?.DEV) {
    (globalThis as any)[globalKey] = client;
  }

  return client;
})();

export function getSupabase(): SupabaseClient | null {
  return supabase;
}

export function isSupabaseConfigured(): boolean {
  return !!url && !!anon;
}

// Allow-listed origins for OAuth redirects (security hardening)
const ALLOWED_ORIGINS = [
  'https://app.coachd.io',
  'https://staging.coachd.io',
  'https://coachdapp.com',
  'https://www.coachdapp.com',
  'https://app.coachdapp.com',
  'http://localhost:5173',
  'http://localhost:5174',
  'http://localhost:8788',
  'http://127.0.0.1:8788',
];

/**
 * Returns the auth callback URL for the Worker (not SPA).
 * The Worker handles PKCE exchange and sets httpOnly cookies.
 * Prevents open redirect vulnerabilities by ensuring only approved
 * origins can be used for OAuth/email redirects.
 */
export function getAuthRedirect(): string {
  const origin = window.location.origin;

  if (!ALLOWED_ORIGINS.includes(origin)) {
    console.error('🚨 Security: Blocked unauthorized origin:', origin);
    console.error('   Allowed origins:', ALLOWED_ORIGINS);
    // Fallback to first allowed origin to prevent auth failure
    return `${ALLOWED_ORIGINS[0]}/api/auth/callback`;
  }

  // Point to Worker endpoint (not SPA /auth/callback)
  return `${origin}/api/auth/callback`;
}

// Default export for convenience
export default supabase;
